import { store } from '@/store/main';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import blAuth from './bl-auth/bl-auth';
import adminCache from './cache';
import toastTrigger from '@ui/toast/toast-trigger';
import { isLocalhost } from '@/utils/urls';

export const HvHostMap = {
  authorization: String(import.meta.env.VITE_HOSTS_AUTHORIZATION),
  chat: String(import.meta.env.VITE_HOSTS_CHAT),
  datalake: String(import.meta.env.VITE_HOSTS_DATALAKE),
  eventData: String(import.meta.env.VITE_HOSTS_EVENT_DATA),
  registration: String(import.meta.env.VITE_HOSTS_REGISTRATION),
  imageResizer: String(import.meta.env.VITE_HOSTS_IMAGE_RESIZER),
  sessionTracking: String(import.meta.env.VITE_HOSTS_SESSION_TRACKING),
  streamSso: String(import.meta.env.VITE_HOSTS_STREAM_SSO),
  stripe: String(import.meta.env.VITE_HOSTS_STRIPE),
  surveys: String(import.meta.env.VITE_HOSTS_SURVEYS),
};
type HvKey = keyof typeof HvHostMap;
export type HvHost = (typeof HvHostMap)[HvKey];
export const parseHvPath = (host: HvHost, path: string) => {
  return `${host}${path}`;
};

export type Params = {
  url?: string;
  path?: string; // use either url or path
  publicPath?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: Record<string, any> | Record<string, any>[];
  headers?: HeadersInit;
  ignoreResponse?: boolean;
  includeCredentials?: boolean;
  host?: string;
  signal?: AbortSignal;
  authRequired?: boolean;
  useCache?: boolean;
  /** seconds to cache request  */
  cacheTtl?: number;
  isMetrics?: boolean;
  /** returnResponse: true will return the response without any parsing/validating */
  returnResponse?: boolean;
};

export class FetchError extends Error {
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body: any,
    public response: Response
  ) {
    super(response.statusText);
  }
}

const handleJsonResponse = async <T>(res: Response): Promise<T> => {
  if (res.headers.get('Content-Type')?.includes('application/json')) {
    const json = (await res.json()) as T;

    if (!res.ok) {
      throw new FetchError(json, res);
    }

    return json;
  }
  if (res.headers.get('Content-Type')?.includes('text/csv')) {
    return res as T;
  }

  const text = await res.text();
  if (text === 'OK') {
    return {} as T;
  }
  throw new FetchError(text, res);
};

const handleOkResponse = async (res: Response): Promise<boolean> => {
  return res.ok;
};

export const isUnifiedAuth = () => {
  return store.getState().featureFlags.featureFlags[FeatureFlagsEnum.unified_auth];
};

const removeSub = (hostname: string) => {
  return hostname.split('.').splice(-2).join('.');
};

export const isSameOrigin = () => {
  return removeSub(location.hostname) === removeSub(import.meta.env.VITE_API_HOST as string);
};

const getHeaders = (paramsHeaders: Record<string, string> = {}): Record<string, string> => {
  const { 'bl-global-resource': blGlobalResource, ...headers } = paramsHeaders;

  // we need to completely remove the header if it's false so that Greenroom api calls don't fail.
  const globalResourceHeader: Record<string, string> =
    blGlobalResource === 'false' ? {} : { 'bl-global-resource': 'true' };

  // bl-token was manually passed in, skip all the other auth headers
  if (headers['bl-token']) {
    return {
      'Content-Type': 'application/json',
      ...globalResourceHeader,
      ...headers,
    };
  }

  if (isSameOrigin() || !blAuth.accessToken) {
    return {
      'Content-Type': 'application/json',
      ...globalResourceHeader,
      ...headers,
    };
  } else {
    return {
      'Content-Type': 'application/json',
      'bl-auth-method': 'bearer',
      Authorization: `Bearer ${blAuth.accessToken}`,
      ...globalResourceHeader,
      ...headers,
    };
  }
};

const SendRequestWithBody = async <T>(
  method: string,
  {
    url,
    path,
    headers,
    body,
    ignoreResponse,
    host = import.meta.env.VITE_API_HOST,
    // publicPath,
  }: Params
): Promise<T> => {
  try {
    // not sure what public path is or why it's needed
    // if (!publicPath) {
    //   throw new Error('Unauthorized');
    // }

    const res = await fetch(`${host}${path ?? url}`, {
      method: method,
      headers: getHeaders(headers as Record<string, string>),
      body: JSON.stringify(body),
      credentials: isSameOrigin() ? ('include' as RequestCredentials) : undefined,
    });

    if (ignoreResponse) {
      return (await handleOkResponse(res)) as T;
    }

    return await handleJsonResponse<T>(res);
  } catch (e: unknown) {
    if (e instanceof FetchError) {
      throw e;
    }

    throw new FetchError(e, new Response(null, { status: 500 }));
  }
};

type TGetUrlFromPathOptions = {
  isMetrics?: boolean;
  host?: string;
};

const getUrlFromPath = (path: string, options?: TGetUrlFromPathOptions): string => {
  if (options?.host) {
    return `${options.host}${path}`;
  }

  if (!path.startsWith('http')) {
    const isMetrics = !!options?.isMetrics;
    const host = isMetrics ? import.meta.env.VITE_METRICS_API_HOST : import.meta.env.VITE_API_HOST;
    return `${host}${path}`;
  } else {
    return path;
  }
};

const awaitAuth = async () => {
  const token = store.getState()?.user?.idToken;
  let retries = 100;

  if (token) {
    return true;
  }

  while (!store.getState()?.user?.idToken && retries >= 0) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (store.getState()?.user?.idToken) {
      return true;
    }
    retries--;
  }

  return false;
};

export const Get = async <T>({
  url,
  path,
  headers,
  ignoreResponse,
  signal,
  authRequired,
  useCache,
  cacheTtl,
  isMetrics,
  returnResponse,
  host,
}: Params) => {
  try {
    const uri = path ?? url;

    if (!uri) {
      throw new Error('Must provide either a url or a path');
    }

    if (authRequired) {
      const authed = await awaitAuth();
      if (!authed) {
        throw new Error('Unauthorized');
      }
    }

    // not sure what the intent was here but I don't think we need a magic parameter here
    // that throws if not supplied
    // if (!publicPath) {
    //   throw new Error('Unauthorized');
    // }

    const options: RequestInit = {
      method: 'GET',
      headers: getHeaders(headers as Record<string, string>),
      credentials: isSameOrigin() ? 'include' : undefined,
      signal,
      cache: useCache ? adminCache.use(uri, cacheTtl) : 'reload',
    };

    // just a warning to the developer if they are making a metrics call without metrics flag
    // this is because we can be using localhost for our metrics api call locally which will gives us the impression
    // that everything is working but then it breaks when deployed
    if (isLocalhost && !isMetrics && uri.includes('/metrics/')) {
      toastTrigger(
        `
          <div class="S2">
            <div class="margin-b-12">
              Hey, ${blAuth.user?.profile.first_name || 'there'},
            </div>
            <div class="margin-b-12">
              Looks like you have a metrics API call that is not marked as metrics.
            </div>
            <div class="margin-b-12">
              Please add <code style="background-color: yellow; color: red;" class="margin-h-4 padding-h-2">isMetrics: true</code> to the API call parameters. This will ensure the call goes to the correct host.
            </div>
            <div>PATH:</div>
            <div>${uri}</div>
            <div class="margin-t-12">Thanks! :)</div>
            <div>P.S. This message will only show when running on localhost.</div>
          </div>
        `,
        { type: 'error', autoClose: false, toastId: uri, closeOnClick: true }
      );
    }

    const res = await fetch(getUrlFromPath(uri, { isMetrics, host }), options);

    if (returnResponse) {
      return res as T;
    }

    if (ignoreResponse) {
      return (await handleOkResponse(res)) as T;
    }

    return await handleJsonResponse<T>(res);
  } catch (e: unknown) {
    if (useCache) {
      adminCache.delete(path ?? url);
    }
    if (e instanceof FetchError) {
      throw e;
    }
    if (e instanceof DOMException && e.name.startsWith('AbortError')) {
      throw e;
    }
    throw new FetchError(e, new Response(null, { status: 500 }));
  }
};

export const PostHv = async <T>({ url, path, headers, body, host }: Params): Promise<T> => {
  return SendRequestWithBody<T>('POST', { url, path, headers, body, host });
};

export const Put = async <T>({ url, path, headers, body, ignoreResponse = false, host }: Params): Promise<T> => {
  return SendRequestWithBody<T>('PUT', { url, path, headers, body, ignoreResponse, host });
};

export const Post = async <T>({ url, path, headers, body, ignoreResponse, host }: Params): Promise<T> => {
  return SendRequestWithBody<T>('POST', { url, path, headers, body, ignoreResponse, host });
};

export const Patch = async <T>({ url, path, headers, body, ignoreResponse = false }: Params): Promise<T> => {
  return SendRequestWithBody<T>('PATCH', { url, path, headers, body, ignoreResponse });
};

export const Delete = async <T>({ url, path, headers, body, ignoreResponse }: Params): Promise<T> => {
  return SendRequestWithBody<T>('DELETE', { url, path, headers, body, ignoreResponse });
};

export const getPagingParamsFromPageNum = (page: `${number}`, pageSize: number) => {
  return {
    offset: (Number(page) - 1) * pageSize,
    limit: pageSize,
  };
};
